import { DataCategoryDataTypes, DataCategoryDataTypesSaveModel, DataSubjectDataCategory, ProcessingActivityModel } from "../ProcessingActivity.types";
import { DataCategoryViewModel } from "./DataCategoriesStep.types";
import { ValidationError } from "../../common/validationError";
import { useTranslation } from "../../localization/useTranslation";
import { useSelectableDataCategoriesQuery } from "../hooks/useSelectableDataCategoriesQuery";
import { deleteHttp, post } from "../../common/api/apiShared";
import { useOptimisticUpdate } from "../../common/hooks/useOptimisticUpdate";
import { useUpdateProcessingActivityModel } from "../hooks/useUpdateProcessingActivityModel";
import { DotLegalSelectOption } from "../../common/components/dotLegalMultiSelect/DotLegalMultiSelect.types";
import { useState } from "react";
import { useDataSubjectsQuery } from "../../common/hooks/useSelectableItemQueries";
import { isNullOrWhitespace } from "../../common/stringOperations";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";

export function useDataCategoriesStepHooks(
    processingActivityId: string,
    dataCategories: Array<DataCategoryDataTypes>,
    dataSubjects: Array<string>,
    dataSubjectDataCategories: Array<DataSubjectDataCategory>
) {
    let dataCategoriesQuery = useSelectableDataCategoriesQuery();
    let dataSubjectsQuery = useDataSubjectsQuery();
    const addStandardDataCategoryMutation = useDotLegalMutation(addStandardDataCategory);
    const removeStandardDataCategoryMutation = useDotLegalMutation(removeStandardDataCategory);
    const updateProcessingActivityModel = useUpdateProcessingActivityModel(processingActivityId);
    const optimisticUpdate = useOptimisticUpdate();
    let dataCategoriesData: Array<DataCategoryViewModel> | undefined;
    let dataSubjectsOptions: Array<DotLegalSelectOption> = [];
    const [dataSubjectId, setDataSubjectId] = useState<string | null>(null);

    const getDataCategory = (dataCategoryId: string) => {
        if (!isNullOrWhitespace(dataSubjectId)) {
            let dataSubjectDataCategory = dataSubjectDataCategories.find((x) => x.dataCategoryId === dataCategoryId && x.dataSubjectId === dataSubjectId);
            if (dataSubjectDataCategory) {
                return { dataTypeIds: dataSubjectDataCategory.dataTypeIds, canSpecifyDataTypesForAllDataSubjects: true };
            }
        } else {
            let dataCategory = dataCategories.find((x) => x.dataCategoryId === dataCategoryId);
            if (dataCategory) {
                return { dataTypeIds: dataCategory.dataTypeIds, canSpecifyDataTypesForAllDataSubjects: dataCategory.canSpecifyDataTypesForAllDataSubjects };
            }
        }

        return undefined;
    };

    if (dataCategoriesQuery.allDataCategories) {
        dataCategoriesData = dataCategoriesQuery.allDataCategories!.map((d) => {
            const dataCategory = getDataCategory(d.id);
            return {
                name: d.name,
                id: d.id,
                description: d.description,
                classification: d.classification,
                isSelected: dataCategory ? true : false,
                selectedDataTypeIds: dataCategory?.dataTypeIds ?? [],
                dataTypeIds: d.dataTypeIds ?? [],
                canSpecifyDataTypesForAllDataSubjects: !isNullOrWhitespace(dataSubjectId)
                    ? true
                    : (dataCategory?.canSpecifyDataTypesForAllDataSubjects ?? false),
            };
        });

        if (dataSubjectsQuery.data) {
            dataSubjectsOptions = dataSubjects.map((d) => {
                return {
                    name: dataSubjectsQuery.data!.find((x) => x.id === d)!.name,
                    id: d,
                };
            });
        }
    }

    async function onAddStandardDataCategory(dataCategoryDataTypes: DataCategoryDataTypesSaveModel, dataSubjectId: string | null) {
        const newProcessingActivityModel = updateProcessingActivityModel.update((model) => {
            if (dataSubjectId !== null && dataSubjectId !== "all") {
                model.dataSubjectDataCategories.push(
                    new DataSubjectDataCategory(dataCategoryDataTypes.dataCategoryId, dataSubjectId, dataCategoryDataTypes.dataTypeIds)
                );
            }

            model.dataCategories.push({ ...dataCategoryDataTypes, canSpecifyDataTypesForAllDataSubjects: false });
        });
        await optimisticUpdate.updateWithProcessingActivityResponse(newProcessingActivityModel, addStandardDataCategoryMutation.mutateAsync, {
            processingActivityId: newProcessingActivityModel.id,
            dataCategoryId: dataCategoryDataTypes.dataCategoryId,
            forDataSubjectId: dataSubjectId,
        });
    }

    async function onRemoveStandardDataCategory(id: string, dataSubjectId: string | null) {
        const newProcessingActivityModel = updateProcessingActivityModel.update((model) => {
            if (dataSubjectId !== null && dataSubjectId !== "all")
                model.dataSubjectDataCategories = model.dataSubjectDataCategories.filter((d) => d.dataCategoryId !== id);

            model.dataCategories = model.dataCategories.filter((d) => d.dataCategoryId !== id);
        });
        await optimisticUpdate.updateWithProcessingActivityResponse(newProcessingActivityModel, removeStandardDataCategoryMutation.mutateAsync, {
            processingActivityId: newProcessingActivityModel.id,
            dataCategoryId: id,
            forDataSubjectId: dataSubjectId,
        });
    }

    return {
        dataCategoriesData,
        isLoading: dataCategoriesQuery.isLoading,
        onAddStandardDataCategory,
        onRemoveStandardDataCategory,
        dataSubjectsOptions,
        isLoadingSubjects: dataSubjectsQuery.isLoading,
        dataSubjectId,
        setDataSubjectId,
    };
}

export function useValidateDataCategoriesStep() {
    const { translateString } = useTranslation();
    return (model: ProcessingActivityModel): Array<ValidationError> => {
        const result: Array<ValidationError> = [];

        if (model.dataCategories.length === 0) {
            result.push(new ValidationError("DataCategories", translateString("dataCategoriesRequired")));
        }

        return result;
    };
}

async function addStandardDataCategory(data: { processingActivityId: string; dataCategoryId: string; forDataSubjectId: string | null }) {
    return await post<ProcessingActivityModel>("/processingactivity/" + data.processingActivityId + "/standardDatacategory", {
        dataCategoryId: data.dataCategoryId,
        forDataSubjectId: data.forDataSubjectId,
    });
}

async function removeStandardDataCategory(data: { processingActivityId: string; dataCategoryId: string; forDataSubjectId: string | null }) {
    return await deleteHttp<ProcessingActivityModel>(
        "/processingactivity/" + data.processingActivityId + "/standardDatacategory/" + data.dataCategoryId + "?forDataSubjectId=" + data.forDataSubjectId
    );
}
